/* eslint-disable no-unused-vars, autofix/no-unused-vars */

import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  CancelTokenSource,
} from 'axios';
import querystring from 'querystring';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { LoadablePromise } from 'recoil';
import axiosInstance from '../common/axiosInterceptor';
import { IUserAtom } from '../recoil/atoms/user';
import { Logger } from './Logger';
export enum loadableEnum {
  EMPTY = '',
  LOADING = 'loading',
  HAS_VALUE = 'hasValue',
  HAS_ERROR = 'hasError',
  CANCELED = 'canceled',
}

// interface IUseMakePHPRequestReturn {
//   // eslint-disable-next-line no-unused-vars
//   [index: number]: AxiosResponse | undefined | ((value: any) => void) | loadableEnum;
// }

interface IMakeRequest {
  /** Call name of PHP API */
  methodName: string;
  /** Payload to send to API server */
  payload?: { [key: string]: any }; // indexable object
}

// Alternative array return version (not as integrated into intellisense)
// type TMakeRequestReturn = [
//   /** Axios response if successful */
//   AxiosResponse | undefined,
//   /** Axios error if failed */
//   AxiosError | Error | undefined,
//   /** Status of request */
//   loadableEnum
// ];

interface IMakeRequestReturn {
  /** Axios response if successful */
  response?: AxiosResponse | undefined;
  /** Axios error if failed */
  exception?: AxiosError | Error | undefined;
  /** Status of request */
  status: loadableEnum;
}

/** IUseMakeRequest returns callable requester */
export type TUseMakeRequestReturn = (
  props: IMakeRequest
) => Promise<IMakeRequestReturn>;

interface IUseMakePHPRequest {
  /** User atom content */
  userAtom:
    | IUserAtom
    | Error
    | LoadablePromise<IUserAtom | Record<string, unknown>>;
  /** Sets current status of request */
  setStatus?: Dispatch<SetStateAction<loadableEnum>>;
  /** Sets canceler used to cancel Axios request */
  setCancelTokenSource?: Dispatch<
    SetStateAction<CancelTokenSource | undefined>
  >;
}

/**
 * Custom hook used to call PHP APIs
 *
 * @see https://itnext.io/how-to-create-a-react-hook-to-make-ajax-calls-5d5052e08269
 *
 * @returns makeRequest callback used to send payload
 */
export const useMakePHPRequest = ({
  userAtom,
  setStatus,
  setCancelTokenSource,
}: IUseMakePHPRequest): TUseMakeRequestReturn =>
  // only render once per userAtom via useCallback
  useCallback(
    async ({
      methodName,
      payload = {},
    }: IMakeRequest): Promise<IMakeRequestReturn> => {
      const cancelTokenSource = axios.CancelToken.source();

      // set local state since setState is asynchronous
      let status: loadableEnum;

      // set both since setStatus is asynchronous
      status = loadableEnum.LOADING;
      setStatus?.(status);

      if (Object.keys(userAtom).length) {
        const userAtomContent = userAtom as IUserAtom;
        const request: AxiosRequestConfig =
          Object.keys(payload).length === 0
            ? {
                method: 'post',
                url:
                  `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php?` +
                  querystring.stringify({
                    call: methodName,
                    businessid: userAtomContent.businessid,
                    apiKey: userAtomContent.apikey,
                    ...payload,
                  }),
                headers: {
                  'content-type':
                    'application/x-www-form-urlencoded;charset=utf-8',
                },
                withCredentials: true,
                cancelToken: cancelTokenSource.token,
              }
            : {
                method: 'post',
                url: `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`,
                data: querystring.stringify({
                  call: methodName,
                  businessid: userAtomContent.businessid,
                  apiKey: userAtomContent.apikey,
                  ...payload,
                }),
                headers: {
                  'content-type':
                    'application/x-www-form-urlencoded;charset=utf-8',
                },
                withCredentials: true,
                cancelToken: cancelTokenSource.token,
              };

        try {
          // set cancel token source
          setCancelTokenSource?.(cancelTokenSource);

          // execute request
          const response = await axiosInstance(request);

          // cancellation tester
          // axios({
          //   method: 'post',
          //   url: 'http://httpstat.us/200?sleep=3000',
          //   cancelToken: cancelTokenSource.token,
          // });

          // clear cancel token
          setCancelTokenSource?.(undefined);

          // detect error from php back-end
          if (
            (typeof response.data?.meta?.success === 'boolean' &&
              !response.data.meta.success) || // see if we can parse response and response has failure
            (typeof response.data?.error === 'string' && response.data?.error) // or error detected
          ) {
            status = loadableEnum.HAS_ERROR;
            Logger.error(
              {
                pathname: window.location.pathname,
                businessid: userAtomContent.businessid,
                apiKey: userAtomContent.apikey,
                userid: userAtomContent.user?.userid,
                methodName,
                request,
                response: response.data,
                // response,
              },
              'useMakePHPRequest'
            );
          } else {
            status = loadableEnum.HAS_VALUE;
            Logger.info(
              {
                pathname: window.location.pathname,
                businessid: userAtomContent.businessid,
                apiKey: userAtomContent.apikey,
                userid: userAtomContent.user?.userid,
                methodName,
                request,
                // response,
              },
              'useMakePHPRequest'
            );
          }

          setStatus?.(status);

          return {
            response,
            status,
          };
        } catch (exception) {
          if (axios.isCancel(exception)) {
            // log cancellation
            Logger.info(
              {
                pathname: window.location.pathname,
                businessid: userAtomContent.businessid,
                apiKey: userAtomContent.apikey,
                userid: userAtomContent.user?.userid,
                methodName,
                message: 'Canceled request',
                request,
                exception,
              },
              'useMakePHPRequest'
            );

            // clear loading status
            status = loadableEnum.CANCELED;
          } else {
            // log error
            Logger.error(
              {
                pathname: window.location.pathname,
                businessid: userAtomContent.businessid,
                apiKey: userAtomContent.apikey,
                userid: userAtomContent.user?.userid,
                methodName,
                message: 'Failed request',
                request,
                exception,
              },
              'useMakePHPRequest'
            );

            // set error status
            status = loadableEnum.HAS_ERROR;
          }

          // set status
          setStatus?.(status);

          // set exception
          return {
            exception,
            status,
          };
        }
      } else {
        Logger.error(
          {
            pathname: window.location.pathname,
            methodName,
            message: 'AJAX not executed - user atom empty',
            payload,
            userAtom,
          },
          'useMakePHPRequest'
        );

        // clear loading status
        status = loadableEnum.HAS_ERROR;
        setStatus?.(status);
        return {
          exception: new Error('AJAX not executed - user atom empty'),
          status,
        };
      }
    },
    [userAtom]
  );
